/**
 * Owning-panel visibility, as context (REQ-STATUS-099).
 *
 * # Why context rather than a prop
 *
 * The thing that needs to know "can anyone see this?" is a **hook several
 * levels down** — :func:`useLiveFrameRefresh`, reached through
 * :func:`useSystemStatusSnapshot` and, in Entity Details, through a selection
 * dispatch and a per-kind detail component. Passing a boolean down that chain is
 * three levels of prop drilling (coding-standards §2.4 caps it at two) repeated
 * per consumer, and every new consumer is a chance to forget it — the failure
 * mode being a silent, invisible cost rather than a visible bug.
 *
 * With the panel supplying its visibility here, the primitive reads it directly
 * and the rule cannot be forgotten by a caller that does not know it exists.
 *
 * # What it is for
 *
 * Gate *work* on this — fetches, timers, animation. Dockview keeps non-active
 * tabs **mounted**, so "my hooks are running" says nothing about whether an
 * operator is looking. ``system-status`` ships behind the map in the default
 * layout's tab group (:file:`./presets`), which made that distinction expensive:
 * before this existed the live pass measured four ``/api/system/snapshot`` reads
 * in 25 s in a session where the System panel was never opened.
 *
 * Do **not** gate state on it. A panel that discards state when backgrounded
 * loses it on a tab switch, which is what dockview's keep-mounted avoids.
 *
 * Defaults to ``true`` with no provider — a panel outside a dockview host
 * (tests, storybook) counts as visible, which is the fail-safe direction for a
 * freshness gate: the cost of being wrong is one request, not a frozen display.
 *
 * Shaped as the twin of :file:`@/system-status/use-system-status-stream`: the
 * ``.tsx`` module exports the provider component exclusively, while the
 * context value and the access hook live here. Same fast-refresh discipline.
 *
 * Cross-references:
 *   - REQ-STATUS-099 (what this gates); ADR-024 §2 (the panel layout).
 *   - :func:`usePanelVisible` — the dockview subscription behind it.
 */
import { createContext, useContext } from "react";

/** True when the owning panel is the active tab of its group (or unhosted). */
export const PanelVisibilityContext = createContext<boolean>(true);

/**
 * Whether the panel owning this subtree is currently visible.
 *
 * @returns ``true`` when visible, or when rendered outside any provider.
 */
export function useOwningPanelVisible(): boolean {
  return useContext(PanelVisibilityContext);
}
